import React from "react";
import { ART_SIZE, PICTURE_ART, PictureId } from "./pictureArt.ts";

interface PictureArtProps {
    id: PictureId;
    /**
     * The picture's name for a screen reader. Without it the picture is hidden from
     * them, as it is while it is covered (ADR 0133 §5); with it, it is an image.
     */
    label?: string;
}

/**
 * One picture as an inline SVG of square cells (ADR 0133 §5): a background rectangle
 * in the "." colour, then a rectangle for every other pixel, with crisp edges so the
 * cells stay sharp at any size. The fills are the art's own and do not follow the theme.
 * Pure of its props, so it is drawn once and a lifted tile does not redraw it.
 */
export const PictureArt = React.memo(function PictureArt({ id, label }: PictureArtProps) {
    const { palette, rows } = PICTURE_ART[id];
    const cells: React.ReactElement[] = [];
    rows.forEach((row, y) => {
        for (let x = 0; x < ART_SIZE; x++) {
            const ch = row[x];
            if (ch !== ".") {
                cells.push(<rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill={palette[ch]} />);
            }
        }
    });

    return (
        <svg
            className="pictureArt"
            viewBox={`0 0 ${ART_SIZE} ${ART_SIZE}`}
            shapeRendering="crispEdges"
            {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": "true", focusable: "false" })}
        >
            <rect x={0} y={0} width={ART_SIZE} height={ART_SIZE} fill={palette["."]} />
            {cells}
        </svg>
    );
});

export default PictureArt;
